import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Award, Star, Trophy, Target, Calendar, Flame } from 'lucide-react';
import { supabase, Database } from '../../lib/supabase';

type Achievement = Database['public']['Tables']['achievements']['Row'];
type ChildAchievement = Database['public']['Tables']['child_achievements']['Row'] & {
  achievements: Achievement;
};

interface ChildContextType {
  selectedChild: string;
  selectedChildData: any;
  soundEnabled: boolean;
  isDarkMode: boolean;
  theme: any;
}

const achievementIcons = {
  Star,
  Trophy,
  Target,
  Award,
  Calendar,
  Flame,
};

export function ChildAchievementsPage() {
  const { selectedChild, selectedChildData, isDarkMode, theme } = useOutletContext<ChildContextType>();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [childAchievements, setChildAchievements] = useState<ChildAchievement[]>([]);
  const [dailyStreak, setDailyStreak] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAchievements();
    fetchChildAchievements();
    fetchDailyStreak();
  }, [selectedChild]);

  const fetchAchievements = async () => {
    try {
      const { data, error } = await supabase
        .from('achievements')
        .select('*')
        .order('points_required', { ascending: true });

      if (error) throw error;
      setAchievements(data || []);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
  };

  const fetchChildAchievements = async () => {
    if (!selectedChild) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('child_achievements')
        .select(`
          *,
          achievements (*)
        `)
        .eq('child_id', selectedChild)
        .order('earned_at', { ascending: false });

      if (error) throw error;
      setChildAchievements(data || []);
    } catch (error) {
      console.error('Error fetching child achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyStreak = async () => {
    if (!selectedChild) return;

    try {
      const { data, error } = await supabase
        .from('daily_streaks')
        .select('*')
        .eq('child_id', selectedChild)
        .single();

      if (error) throw error;
      setDailyStreak(data);
    } catch (error) {
      console.error('Error fetching daily streak:', error);
    }
  };

  const isAchievementEarned = (achievementId: string) => {
    return childAchievements.some(ca => ca.achievement_id === achievementId);
  };

  const canEarnAchievement = (achievement: Achievement) => {
    return selectedChildData.total_points >= achievement.points_required;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4 text-[var(--text-primary)] font-baloo">
          Your Amazing Achievements!
        </h1>
        <p className="text-xl text-[var(--text-secondary)]">
          Look at all the awesome things you've accomplished, {selectedChildData.name}!
        </p>
      </div>

      {/* Achievement Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`rounded-3xl p-6 text-white text-center shadow-xl ${
          theme.mode === 'high-contrast' 
            ? 'bg-yellow-500 border-4 border-white'
            : 'bg-gradient-to-r from-yellow-400 to-orange-500'
        }`}>
          <Trophy className="w-12 h-12 mx-auto mb-3" />
          <div className="text-3xl font-bold font-baloo">{childAchievements.length}</div>
          <div className="text-lg">Achievements Earned</div>
        </div>

        <div className={`rounded-3xl p-6 text-white text-center shadow-xl ${
          theme.mode === 'high-contrast'
            ? 'bg-red-500 border-4 border-white'
            : 'bg-gradient-to-r from-orange-400 to-red-500'
        }`}>
          <Flame className="w-12 h-12 mx-auto mb-3" />
          <div className="text-3xl font-bold font-baloo">{dailyStreak?.current_streak || 0}</div>
          <div className="text-lg">Day Learning Streak</div>
        </div>

        <div className={`rounded-3xl p-6 text-white text-center shadow-xl ${
          theme.mode === 'high-contrast'
            ? 'bg-blue-500 border-4 border-white'
            : 'bg-gradient-to-r from-green-400 to-blue-500'
        }`}>
          <Target className="w-12 h-12 mx-auto mb-3" />
          <div className="text-3xl font-bold font-baloo">{dailyStreak?.longest_streak || 0}</div>
          <div className="text-lg">Best Streak Ever</div>
        </div>
      </div>

      {/* Achievements Grid */}
      <div>
        <h2 className="text-3xl font-bold text-center mb-8 text-[var(--text-primary)] font-baloo">
          All Achievements
        </h2>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="mt-4 text-lg text-[var(--text-secondary)]">
              Loading your achievements...
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {achievements.map((achievement) => {
              const earned = isAchievementEarned(achievement.id);
              const canEarn = canEarnAchievement(achievement);
              const IconComponent = achievementIcons[achievement.icon as keyof typeof achievementIcons] || Award;

              return (
                <div
                  key={achievement.id}
                  className={`relative rounded-3xl p-6 shadow-xl border-4 transition-all duration-300 ${
                    theme.reducedMotion ? '' : 'transform hover:scale-105'
                  } ${
                    earned
                      ? theme.mode === 'high-contrast'
                        ? 'border-green-400 bg-green-500 text-white'
                        : 'border-green-400 bg-[var(--bg-secondary)]'
                      : canEarn
                      ? theme.mode === 'high-contrast'
                        ? 'border-yellow-400 bg-yellow-500 text-black'
                        : 'border-yellow-400 bg-[var(--bg-secondary)]'
                      : 'border-[var(--border-color)] bg-[var(--bg-secondary)]'
                  }`}
                >
                  {earned && (
                    <div className="absolute -top-3 -right-3 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <Star className="w-5 h-5 text-white" />
                    </div>
                  )}

                  <div className="text-center">
                    <div
                      className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg ${
                        earned
                          ? 'bg-green-500'
                          : canEarn
                          ? 'bg-yellow-500'
                          : theme.mode === 'high-contrast'
                            ? 'bg-gray-300'
                            : 'bg-[var(--bg-tertiary)]'
                      }`}
                      style={{ backgroundColor: earned ? achievement.badge_color : undefined }}
                    >
                      <IconComponent
                        className={`w-10 h-10 ${
                          earned || canEarn 
                            ? 'text-white' 
                            : theme.mode === 'high-contrast'
                            ? 'text-black'
                            : 'text-[var(--text-muted)]'
                        }`}
                      />
                    </div>

                    <h3 className={`text-xl font-bold mb-2 ${
                      theme.mode === 'high-contrast'
                        ? earned || canEarn ? 'text-white' : 'text-black'
                        : 'text-[var(--text-primary)]'
                    } font-baloo`}>
                      {achievement.name}
                    </h3>
                    <p className={`mb-4 text-lg ${
                      theme.mode === 'high-contrast'
                        ? earned || canEarn ? 'text-white' : 'text-black'
                        : 'text-[var(--text-secondary)]'
                    }`}>
                      {achievement.description}
                    </p>

                    <div className="flex items-center justify-center space-x-2 text-lg mb-4">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <span className={`font-semibold ${
                        theme.mode === 'high-contrast'
                          ? earned || canEarn ? 'text-white' : 'text-black'
                          : 'text-[var(--text-primary)]'
                      }`}>
                        {achievement.points_required} points needed
                      </span>
                    </div>

                    {earned && (
                      <div className="inline-flex items-center px-4 py-2 rounded-full text-lg font-bold bg-green-500 text-white shadow-lg font-baloo">
                        <Trophy className="w-5 h-5 mr-2" />
                        You Did It!
                      </div>
                    )}

                    {!earned && canEarn && (
                      <div className="inline-flex items-center px-4 py-2 rounded-full text-lg font-bold bg-yellow-500 text-white shadow-lg font-baloo">
                        <Target className="w-5 h-5 mr-2" />
                        Almost There!
                      </div>
                    )}

                    {!earned && !canEarn && (
                      <div className={`font-semibold ${
                        theme.mode === 'high-contrast' ? 'text-black' : 'text-[var(--text-muted)]'
                      } font-baloo`}>
                        Keep learning to unlock!
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Achievements */}
      {childAchievements.length > 0 && (
        <div>
          <h2 className="text-3xl font-bold text-center mb-6 text-[var(--text-primary)] font-baloo">
            Your Latest Wins!
          </h2>
          <div className="rounded-3xl shadow-xl border-4 border-purple-200 overflow-hidden bg-[var(--bg-secondary)]">
            <div className="max-h-80 overflow-y-auto">
              {childAchievements.slice(0, 5).map((childAchievement, index) => {
                const IconComponent = achievementIcons[childAchievement.achievements.icon as keyof typeof achievementIcons] || Award;
                
                return (
                  <div key={childAchievement.id} className={`p-6 ${
                    index !== childAchievements.length - 1 ? 'border-b border-[var(--border-color)]' : ''
                  }`}>
                    <div className="flex items-center space-x-4">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: childAchievement.achievements.badge_color }}
                      >
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="text-xl font-bold text-[var(--text-primary)] font-baloo">
                          {childAchievement.achievements.name}
                        </div>
                        <div className="text-lg text-[var(--text-secondary)]">
                          {childAchievement.achievements.description}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-lg text-[var(--text-muted)]">
                          {new Date(childAchievement.earned_at).toLocaleDateString()}
                        </div>
                        <div className="text-2xl">🎉</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}